import { useCallback, useMemo, useRef } from 'react';
import format from 'date-fns/format';
import it from 'date-fns/locale/it';
import { useFormContext, FieldValues, ErrorOption } from 'react-hook-form';
import omitBy from 'lodash.omitby';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';

import { useAnalytics, useWizard } from '@restworld/utils-common';
import { Language, Worker } from '@restworld/utility-types';
import {
  WorkerSignupAcceptance,
  buildWorkerSignupAcceptance,
  generateSignupIdempotencyKey,
  signupAcceptanceErrorOf,
  useWorkersService,
} from '@restworld/data-services';
import { showToast } from '@restworld/ui-ds-base';
import { useAuth } from '@restworld/feature-authentication';

import { WizardExperienceInputConfirmModal, WizardLastConfirm } from '../wizard-step.components';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';
import { conflictCopyKeys, conflictFieldsFrom } from '../../worker-application-wizard.conflict-fields';
import { CoverLetterEntry } from '../../../cover-letter-application';
import { useApplyJob } from '../../../../hooks/use-apply-job';
import { entryPointSignupConsentAtom } from '../../../worker-entry-point/worker-entry-point.atoms';
import { useWizardShowsSignupBoxes } from './use-wizard-signup-boxes';

export const stepsMapping: {
  [key: number]: string;
} = {
  0: 'personal_information',
  1: 'worker_location',
  2: 'gender_birthdate_and_contact_information',
  3: 'means_of_transport',
  4: 'do_you_have_worker_experience',
  5: 'work_experience',
  6: 'worker_education_and_trainings',
  7: 'certificates',
  8: 'languages',
  9: 'media',
  10: 'job_search_urgency',
  11: 'worker_introduction',
  12: 'worker_job_preferences',
  13: 'social_links',
};

export const useFormInputsHandler = ({ entryTime }: { entryTime?: number; formHeaderTitle?: string }) => {
  const { nextStep, activeStep, goToStep, isLastStep } = useWizard();
  const { setError, clearErrors } = useFormContext();
  const { t } = useTranslation();
  const {
    setOverlayModalContent,
    setWorkerId,
    workerId,
    setLoading,
    setConflictFields,
    userModifying,
    setIsFirstStepSuccessful,
    userConfirmation,
    getUserConfirmation,
  } = useWorkerApplicationCxt();
  const router = useRouter();
  const query = router?.['query'];
  const queryId = useMemo(() => query?.['id'], [query]);

  const isSpontaneousEntry = useMemo(() => query?.['isSpontaneousEntry'] === 'true', [query]);
  const workerService = useWorkersService();
  const { user } = useAuth();
  const { initializeAuthentication } = useAuth();
  const { applyJobPosition } = useApplyJob({
    referralCode: query['scout'],
  });
  const { trackEvent } = useAnalytics();
  const [entryPointSignupConsent, setEntryPointSignupConsent] = useAtom(entryPointSignupConsentAtom);
  const showsSignupBoxes = useWizardShowsSignupBoxes();
  // One key per form attempt: a retry of the first step after an error sends the same key.
  const idempotencyKeyRef = useRef<string | null>(null);

  /**
   * The boxes behind this worker creation (RW-422). The first step shows them on `/candidati`, and
   * on a job page when the entry point's answer is missing (see `useWizardShowsSignupBoxes`);
   * otherwise the answer ticked on the entry point travels in `entryPointSignupConsentAtom`.
   */
  const signupAcceptanceFrom = useCallback(
    (values: FieldValues): WorkerSignupAcceptance | undefined => {
      if (showsSignupBoxes && values['tAndC']) {
        if (!idempotencyKeyRef.current) idempotencyKeyRef.current = generateSignupIdempotencyKey();
        return buildWorkerSignupAcceptance(values['marketingAndTracking'], idempotencyKeyRef.current);
      }
      if (entryPointSignupConsent) {
        return buildWorkerSignupAcceptance(
          entryPointSignupConsent.marketingAndTracking,
          entryPointSignupConsent.idempotencyKey
        );
      }
      return undefined;
    },
    [entryPointSignupConsent, showsSignupBoxes]
  );

  const handleCreateWorker = useCallback(
    async (values: Partial<Worker>, acceptance?: WorkerSignupAcceptance) => {
      if (workerId && user?.type !== 'lead') {
        nextStep();
        return;
      }
      try {
        setLoading(true);
        setConflictFields([]);
        await workerService
          .createWorker(acceptance ? { ...values, acceptance } : values)
          .then(async (r) => {
            const jwt = r.data?.jwt;
            const workerIdCp = r.data?.worker?.user_id ?? '';
            if (workerIdCp) {
              setWorkerId(workerIdCp);
              // Used: the next signup in this tab starts from its own boxes and its own key.
              setEntryPointSignupConsent(null);
              if (queryId) {
                applyJobPosition({
                  [!isSpontaneousEntry ? 'jobPositionId' : 'restaurantId']: queryId as string,
                  workerIdArg: workerIdCp,
                  source: 'wizard',
                });
              } else {
                initializeAuthentication(jwt);
              }
            }
            setIsFirstStepSuccessful(true);
            nextStep();
          })
          .catch(async (err) => {
            const acceptanceError = signupAcceptanceErrorOf(err);
            if (acceptanceError === 'signup_texts_outdated') {
              showToast(t('common:terms_and_conditions.texts_outdated'), 'error', { position: 'top-right' });
              return;
            }
            if (acceptanceError === 'signup_terms_not_accepted') {
              setError('tAndC', { message: t('common:accept_terms_and_conditions'), type: 'custom' });
              return;
            }
            if (err.code === 409 && err?.data?.user_id) {
              const conflictFields = conflictFieldsFrom(err?.data?.conflict_fields);
              setConflictFields(conflictFields);
              conflictFields.forEach((field) =>
                setError(field, {
                  message: t(conflictCopyKeys[field]),
                  type: 'custom',
                })
              );
            }
            if (err?.data?.['errors']) {
              Object.keys(err.data['errors']).map((v) => {
                const errorS = err.data['errors'][v][0];
                if (errorS) {
                  const message = `${v} ${errorS}`;
                  setError(v, { message });
                }
                return errorS;
              });
            }
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        console.error(error, 'error posting first step data');
      }
    },
    [
      applyJobPosition,
      initializeAuthentication,
      isSpontaneousEntry,
      nextStep,
      queryId,
      setConflictFields,
      setEntryPointSignupConsent,
      setError,
      setIsFirstStepSuccessful,
      setLoading,
      setWorkerId,
      t,
      user?.type,
      workerId,
      workerService,
    ]
  );
  const handleUpdateWorker = useCallback(
    async (values: FieldValues) => {
      setLoading(true);
      clearErrors();
      // Goes with `clearErrors()`: this path re-sets errors on the same first-step fields (422),
      // and a conflict left over from an earlier attempt would put a login link under one of
      // them. Reached on step 0 once "previous" has set `userModifying`.
      setConflictFields([]);
      try {
        let valuesCp = { ...values };
        valuesCp = Object.keys(valuesCp).reduce((prev, curr) => {
          let workerValues = valuesCp[curr as keyof Worker] as any;
          const emptyObject = `{}`;
          if (multiFormStepKeys.indexOf(curr) > -1) {
            workerValues = workerValues?.filter((v: FieldValues) => JSON.stringify(v) !== emptyObject);
          }
          return {
            ...prev,
            [curr]: workerValues,
          };
        }, {});
        const newValuesCp = {
          ...valuesCp,
          ...(values['email'] && userModifying ? { emails: [{ email: values['email'], primary: true }] } : {}),
          ...(values['phone_number']
            ? {
                phone_number: values['phone_number'] as string,
              }
            : {}),
          ...(valuesCp['languages']?.length
            ? {
                languages: valuesCp['languages'].reduce((prev: Language[], curr: { language_level_cefr: string }) => {
                  if (curr.language_level_cefr) curr.language_level_cefr = curr.language_level_cefr[0];
                  return [...prev, curr];
                }, [] as Language[]),
              }
            : {}),
          ...(values['displacement_preferences']?.length
            ? {
                displacement_preferences: values['displacement_preferences'].map((id: string) => ({
                  displacement_preference_var: {
                    id,
                  },
                })),
              }
            : {}),
          ...(values['driving_licenses']?.length
            ? {
                driving_licenses: values['driving_licenses'].map((id: string) => ({
                  driving_license_var: {
                    id,
                  },
                })),
              }
            : {}),
          // Flat, unlike the `_var`-wrapped neighbours above. The id is the
          // record's key on the backend, so this shape lets it recognise a
          // declaration the worker already made and keep its original date;
          // wrapped, every save would read as a brand new declaration.
          ...(values['professional_associations']?.length
            ? {
                professional_associations: values['professional_associations'].map((id: string) => ({
                  professional_association: id,
                })),
              }
            : {}),
          ...(values['birthdate']
            ? { birthdate: format(new Date(values['birthdate']), 'yyyy-MM-dd', { locale: it }) }
            : {}),
          ...(valuesCp['research_info']?.length
            ? {
                research_info: {
                  ...valuesCp['research_info'][0],
                  foreground: true,
                  ...(valuesCp['research_info'][0].restaurant_types?.length
                    ? {
                        restaurant_types: valuesCp['research_info'][0].restaurant_types.map((v: string) => ({
                          restaurant_type_var: { id: v },
                        })),
                      }
                    : {}),
                  ...(valuesCp['research_info'][0].occupations?.length
                    ? {
                        occupations: valuesCp['research_info'][0].occupations.map((v: string) => ({ id: v })),
                      }
                    : {}),
                },
              }
            : {}),
          ...(values['shifts']?.length
            ? {
                shifts: values['shifts']?.map((v: string) => ({
                  shift_var: { id: v },
                })),
              }
            : {}),
          ...(values['video']
            ? {
                video: values['video'].map((v: { [x: string]: string }) => {
                  if (!Object.keys(v).length) return undefined;
                  return v;
                }),
              }
            : {}),
          ...(values['picture']
            ? {
                picture: values['picture'].map((v: { [x: string]: string }) => {
                  if (!Object.keys(v).length) return undefined;
                  return v;
                }),
              }
            : {}),
        } as Partial<Worker>;
        await workerService
          .updateWorker({ workerId, params: { ...newValuesCp } })
          .then(() => {
            if (isSpontaneousEntry && activeStep === 3 && !userConfirmation) {
              getUserConfirmation(true);
              setOverlayModalContent({
                visible: true,
                content: <WizardExperienceInputConfirmModal goToStep={goToStep} />,
                key: 'wizard-experience-input-confirm-modal',
              });
              return;
            }
            if (isSpontaneousEntry && activeStep === 5) {
              setOverlayModalContent({
                visible: true,
                content: (
                  <CoverLetterEntry
                    closeEntryPoint={() => setOverlayModalContent({ visible: false })}
                    restaurantID={query['id'] as string}
                    workerId={workerId}
                  />
                ),
                key: 'cover-letter-entry',
              });
              return;
            }
            nextStep();
          })
          .catch((error) => {
            if (error.code === 422 && !!error?.data?.errors) {
              try {
                const errorModified = Object.keys(error.data['errors']).reduce(
                  (_, currOld) => ({
                    ...{
                      [currOld]: error.data['errors'][currOld]
                        .filter((v: { [x: string]: string[] }) => Object.keys(v).length)
                        .map((x: { [x: string]: string[] }) => {
                          let error;
                          if (multiFormStepKeys.includes(currOld)) {
                            error = Object.keys(x).map((y) => ({
                              [y]: {
                                message: `${y} ${x[y].join('')}`,
                                type: 'custom',
                              },
                            })) as ErrorOption;
                          } else {
                            if (typeof x === 'string')
                              error = {
                                message: `${currOld} ${x}`,
                                type: 'custom',
                              };
                            else
                              error = Object.keys(x).reduce(
                                (prev, curr) => ({
                                  ...prev,
                                  message: `${currOld} ${x[curr].join('')}`,
                                  type: 'custom',
                                }),
                                {}
                              );
                          }
                          setError(currOld === 'email' ? 'email' : `root.${currOld}`, error, { shouldFocus: true });
                          return error;
                        }),
                    },
                  }),
                  {}
                );
                return errorModified;
              } catch (error) {
                console.error(error, 'error on fn execution');
              }
            }
            return error;
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        console.error(error, 'error posting data');
      }
    },
    [
      activeStep,
      clearErrors,
      getUserConfirmation,
      setConflictFields,
      goToStep,
      isSpontaneousEntry,
      nextStep,
      query,
      setError,
      setLoading,
      setOverlayModalContent,
      // trackEvent,
      userConfirmation,
      userModifying,
      workerId,
      workerService,
    ]
  );
  const onSubmit = useCallback(
    (values: FieldValues) => {
      // The boxes are not worker fields: they reach the backend only as the `acceptance` object.
      const valuesCopy = omitBy(values, (_, key) => key === 'tAndC' || key === 'marketingAndTracking');
      try {
        trackEvent('questionnaire_progress', {
          step_number: activeStep,
          step_name: stepsMapping[activeStep as number],
          time_spent: entryTime ? (Date.now() - entryTime) / 1000 : undefined,
          next_action: 'continue',
          data: valuesCopy,
        });
        if (isLastStep) {
          setOverlayModalContent({
            visible: true,
            content: <WizardLastConfirm />,
          });
        }
        if (activeStep === 9) {
          nextStep();
          return;
        }
        if (activeStep === 0 && !userModifying) {
          handleCreateWorker(valuesCopy, signupAcceptanceFrom(values));
          return;
        }
        handleUpdateWorker(valuesCopy);
      } catch (error) {
        console.error(error, 'error on submit button');
      }
    },
    [
      activeStep,
      entryTime,
      handleCreateWorker,
      handleUpdateWorker,
      isLastStep,
      nextStep,
      setOverlayModalContent,
      signupAcceptanceFrom,
      trackEvent,
      userModifying,
    ]
  );
  return {
    onSubmit,
  };
};
const multiFormStepKeys = ['past_experiences', 'education_em', 'certificate', 'languages', 'research_info'];
